/**
 * Phase 2.3: Week Summary Screen
 * 
 * Shows the client's weekly summary after coach approval.
 * Combines Progress Tiles (2.2) and Supportive Messages (2.1).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, TrendingUp, Heart } from 'lucide-react';
import Navbar from '@/components/Navbar';
import SupportiveMessage from '@/components/SupportiveMessage';
import { 
  WeightChangeTile, 
  TotalProgressTile, 
  ConsistencyTile, 
  AdjustmentTile,
  WeekendTile,
  ComfortTile 
} from '@/components/ProgressTiles';
import { getBucketSummary, getLowAdherenceVariant } from '@/constants/bucketMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeekSummary({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState(null);

  const fetchWeekSummary = async () => {
    try {
      setLoading(true);
      
      // Fetch check-in history to get total count
      const historyRes = await axios.get(`${API}/checkin/history`);
      const checkinHistory = historyRes.data || [];
      const totalCheckins = checkinHistory.length;
      
      // Fetch recent check-ins (for accurate weight comparison)
      const recentCheckInsRes = await axios.get(`${API}/checkins/my-recent?limit=3`);
      const recentCheckIns = recentCheckInsRes.data || [];
      
      // Latest check-in is the first one
      const latestCheckIn = recentCheckIns[0] || null;
      
      // Previous week's check-in (for weight comparison)
      const previousCheckIn = recentCheckIns[1] || null;
      
      // Determine if this is an early week (Week 1 or 2)
      const isEarlyWeek = totalCheckins <= 2;
      const weekNumber = totalCheckins;
      
      // Fetch user profile for starting weight and goal
      const profileRes = await axios.get(`${API}/auth/me`);
      const profile = profileRes.data;
      
      // Fetch weight logs for display purposes (not for bucket calc)
      const weightRes = await axios.get(`${API}/weight-logs/my-logs`);
      const weightLogs = weightRes.data || [];
      
      // Fetch latest adjustment (if any)
      let latestAdjustment = null;
      try {
        const adjustmentRes = await axios.get(`${API}/adjustments/my-latest`);
        latestAdjustment = adjustmentRes.data;
      } catch (adjError) {
        // No adjustment yet - that's okay
        console.log('No adjustment data yet');
      }

      // Get previous week's weight from check-in data (more accurate for bucket detection)
      const previousWeight = previousCheckIn?.current_weight_kg || previousCheckIn?.weight || null;

      // Determine the bucket based on check-in data (if no adjustment exists)
      // All scores use 1-10 scale where 10 = GOOD, 1 = BAD
      const deriveBucketFromCheckIn = (checkIn, prevWeight, currWeight, startWeight) => {
        if (!checkIn) return 'no_change_needed';
        
        // Convert adherence to percentage (handle both formats)
        const adherence = checkIn.overall_adherence || (checkIn.consistency ? checkIn.consistency * 10 : 100);
        const weekendAdherence = checkIn.weekend_adherence || 100;
        
        // Comfort metrics - CHECK-IN FORMAT: 1-5 scale where:
        // 1 = worst (very hungry/tired/stressed)
        // 5 = best (satisfied/energized/calm)
        // Convert to 1-10 scale by multiplying by 2
        const rawHunger = checkIn.hunger || checkIn.hunger_level || 3.5; // Default to middle
        const rawEnergy = checkIn.energy || checkIn.energy_level || 3.5;
        const rawStress = checkIn.stress_level || 3.5;
        
        // Convert 1-5 → 2-10 scale
        const hunger = rawHunger * 2;
        const energy = rawEnergy * 2;
        const stress = rawStress * 2;
        
        // Calculate weight change metrics
        const weeklyWeightChange = prevWeight && currWeight ? currWeight - prevWeight : null;
        const isPlateauWeight = weeklyWeightChange !== null && Math.abs(weeklyWeightChange) < 0.25;
        
        // Calculate weekly loss percentage (for "losing too fast" detection)
        const weeklyLossPercent = prevWeight && currWeight && prevWeight > currWeight 
          ? ((prevWeight - currWeight) / prevWeight) * 100 
          : 0;
        
        // Priority order matches backend bucketing engine
        
        // Tier 2: Adherence checks first
        // Priority 1: Low adherence (below 80%)
        if (adherence < 80) {
          return 'low_adherence';
        }
        
        // Priority 2: Weekend inconsistency
        // Calculate implied weekday adherence: weekday = (7*overall - 2*weekend) / 5
        const weekday = (7 * adherence - 2 * weekendAdherence) / 5;
        if (weekday >= 85 && weekendAdherence < 70) {
          return 'weekend_inconsistency';
        }
        
        // Tier 3: Comfort metrics (only if adherence is good)
        // Priority 3: High hunger (score 1-3 = very hungry)
        if (hunger <= 3) {
          return 'high_hunger';
        }
        
        // Priority 4: Low energy (score 1-3 = exhausted)
        if (energy <= 3) {
          return 'low_energy';
        }
        
        // Priority 5: High stress (score 1-3 = very stressed)
        if (stress <= 3) {
          return 'high_stress';
        }
        
        // Tier 4: Progress metrics (only if adherence is good AND comfort is OK)
        // Priority 6: Losing too fast (>1% bodyweight per week)
        if (adherence >= 80 && weeklyLossPercent > 1.0) {
          return 'losing_too_fast';
        }
        
        // Priority 7: Plateau (weight flat despite good adherence)
        if (adherence >= 80 && isPlateauWeight) {
          return 'plateau';
        }
        
        // Priority 8: Slow loss (<0.5% per week with good adherence)
        if (adherence >= 80 && weeklyLossPercent > 0 && weeklyLossPercent < 0.5) {
          return 'slow_loss';
        }
        
        // Default: Everything is on track
        return 'no_change_needed';
      };

      // Get current weight for bucket detection
      const currentWeight = latestCheckIn?.weight || latestCheckIn?.current_weight_kg;
      const startWeight = profile?.starting_weight || profile?.startingWeight;

      // PRIORITY for bucket assignment:
      // 1. bucket_assigned from check-in (set by backend bucketing engine)
      // 2. bucket from adjustment record (if adjustment was made)
      // 3. Derive from check-in data (fallback only)
      let bucket = latestCheckIn?.bucket_assigned 
        || latestAdjustment?.bucket 
        || deriveBucketFromCheckIn(latestCheckIn, previousWeight, currentWeight, startWeight);
      
      // Calculate weekly weight change for low_adherence variant selection
      const weeklyWeightChange = previousWeight && currentWeight 
        ? currentWeight - previousWeight 
        : null;
      
      // If bucket is low_adherence, determine the appropriate message variant
      // based on adherence level and weight trend
      let displayBucket = bucket;
      if (bucket === 'low_adherence') {
        const adherence = latestCheckIn?.overall_adherence || 0;
        displayBucket = getLowAdherenceVariant(adherence, weeklyWeightChange);
      }
      
      // Get week dates
      const weekStart = latestCheckIn?.week_start_date 
        ? new Date(latestCheckIn.week_start_date)
        : latestCheckIn?.submitted_at
          ? new Date(latestCheckIn.submitted_at)
          : getWeekStart(new Date());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      setSummaryData({
        hasCheckIn: !!latestCheckIn,
        checkIn: latestCheckIn,
        profile,
        weightLogs,
        previousWeight,
        adjustment: latestAdjustment,
        bucket: displayBucket,  // Use displayBucket for messaging
        baseBucket: bucket,     // Keep original bucket for adjustment logic
        weekStart,
        weekEnd,
        // Early week data
        totalCheckins,
        isEarlyWeek,
        weekNumber
      });
      
    } catch (error) {
      console.error('Error fetching week summary:', error);
      if (error.response?.status === 404) {
        // No check-in yet
        setSummaryData({ hasCheckIn: false });
      } else {
        setError('Unable to load your week summary. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeekSummary();
  }, []); // eslint-disable-line

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    return new Date(d.setDate(diff));
  };

  const formatDateRange = (start, end) => {
    const options = { month: 'short', day: 'numeric' };
    const startStr = start.toLocaleDateString('en-US', options);
    const endStr = end.toLocaleDateString('en-US', options);
    return `${startStr} – ${endStr}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a61e8] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your week summary...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-4xl mb-4">😕</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Oops!</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => fetchWeekSummary()}>Try Again</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // No check-in yet
  if (!summaryData?.hasCheckIn) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/app')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-5xl mb-4">📋</div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                Your First Summary Awaits!
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Complete your first check-in to see your weekly summary here.
              </p>
              <Button 
                onClick={() => navigate('/app/checkin')}
                className="bg-[#1a61e8] hover:bg-[#1a61e8]/90"
              >
                Complete Check-In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Check-in submitted but not yet reviewed by coach
  // For early week clients (Week 1-2), show summary even without full adjustment
  const hasRealAdjustment = summaryData?.adjustment && summaryData.adjustment.status !== 'none' && summaryData.adjustment.bucket;
  const isEarlyWeekClient = summaryData?.isEarlyWeek;
  
  if (summaryData?.hasCheckIn && !hasRealAdjustment && !isEarlyWeekClient) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/app')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-8 text-center">
              <div className="text-5xl mb-4">⏳</div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                Check-in Submitted!
              </h2>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                Your coach is reviewing your check-in. Your weekly summary will appear here once reviewed.
              </p>
              <p className="text-sm text-gray-500">
                This usually happens within 24-48 hours.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Extract data for display
  const { checkIn, profile, previousWeight, adjustment, bucket, weekStart, weekEnd, totalCheckins, isEarlyWeek, weekNumber } = summaryData;
  
  const currentWeight = checkIn?.weight || checkIn?.current_weight_kg;
  
  // Consistency/Adherence normalization
  // overall_adherence is stored as 0-100 percentage
  // We need to display it correctly and pass proper format to tiles
  const rawConsistency = checkIn?.overall_adherence ?? checkIn?.consistency;
  
  // Normalize consistency to percentage (0-100)
  // If value is 1-5, it's old format - convert to percentage
  // If value is 0-100, use as-is
  const consistencyPercent = rawConsistency !== null && rawConsistency !== undefined
    ? (rawConsistency <= 5 ? rawConsistency * 20 : rawConsistency) // 1-5 scale → 20-100%, 0-100 → as-is
    : null;
  
  // Convert percentage to 1-10 scale for ConsistencyTile (which expects 1-10)
  const consistencyScore = consistencyPercent !== null 
    ? Math.round(consistencyPercent / 10) 
    : null;
  
  // Weekend adherence - same normalization
  const rawWeekend = checkIn?.weekend_adherence;
  const weekendScore = rawWeekend !== null && rawWeekend !== undefined
    ? (rawWeekend <= 5 ? rawWeekend * 20 : rawWeekend)
    : null;
  
  // Comfort scores - CHECK-IN FORMAT: 1-5 scale where:
  // 1 = worst (very hungry/tired/stressed)
  // 5 = best (satisfied/energized/calm)
  // We need to convert to 1-10 scale for display where 10 = best
  const rawHunger = checkIn?.hunger || checkIn?.hunger_level;
  const rawEnergy = checkIn?.energy || checkIn?.energy_level;
  const rawStress = checkIn?.stress_level;
  
  // Simple conversion: 1-5 scale → 2-10 scale (multiply by 2)
  // This makes 1→2, 2→4, 3→6, 4→8, 5→10
  // Comfort scores (keep as 1-5 scale from check-in)
  const hungerScore = rawHunger || null;
  const energyScore = rawEnergy || null;
  const stressScore = rawStress || null;
  
  // Adjustment data - use adjustment record as single source of truth
  // Use the actual calorie_change from the adjustment record
  const adjustmentStatus = adjustment?.status || 'none';
  const actualCalorieChange = adjustment?.calorie_change || 0;
  const adjustmentAction = actualCalorieChange < 0 ? 'cut' : (actualCalorieChange > 0 ? 'increase' : 'maintain');
  const hasAdjustment = actualCalorieChange !== 0;
  
  // Cycle awareness
  const showCycleNote = profile?.menstruates && checkIn?.luteal_phase;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/app')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <Calendar className="h-4 w-4" />
            <span>Week of {formatDateRange(weekStart, weekEnd)}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your Week Summary
          </h1>
        </div>

        {/* This Week's Wins Section */}
        <section className="mb-8">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎉</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 text-lg mb-3">
                    This Week's Wins
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const wins = [];
                      
                      // Weight progress win
                      const weightChange = previousWeight && currentWeight ? currentWeight - previousWeight : null;
                      if (weightChange !== null) {
                        if (weightChange < -0.2) {
                          wins.push(`✅ You lost ${Math.abs(weightChange).toFixed(1)}kg this week - great progress!`);
                        } else if (Math.abs(weightChange) <= 0.2) {
                          wins.push(`✅ Weight stayed stable - good consistency!`);
                        }
                      }
                      
                      // Consistency win (check-in uses 1-5 scale)
                      const rawConsistency = checkIn?.consistency || 0;
                      if (rawConsistency >= 4) {
                        const percentage = rawConsistency === 5 ? '90-100%' : '70-90%';
                        wins.push(`✅ You followed your meal plan ${percentage} of the time - fantastic!`);
                      }
                      
                      // Weekend adherence win
                      const weekendAdh = checkIn?.weekend_adherence || 0;
                      if (weekendAdh >= 60) {
                        wins.push(`✅ Weekend consistency was ${weekendAdh}% - weekends are tough, great job!`);
                      }
                      
                      // Hunger management win (1-5 scale: 1=starving, 5=satisfied)
                      const hunger = checkIn?.hunger || 0;
                      if (hunger >= 4) {
                        wins.push(`✅ Hunger levels were well-managed - the food is working for you!`);
                      }
                      
                      // Energy win (1-5 scale: 1=exhausted, 5=energized)
                      const energy = checkIn?.energy || 0;
                      if (energy >= 4) {
                        wins.push(`✅ Energy levels stayed high all week - you're fueling well!`);
                      }
                      
                      // Stress win (1-5 scale: 1=very stressed, 5=very calm)
                      const stress = checkIn?.stress_level || 0;
                      if (stress >= 4) {
                        wins.push(`✅ Stress was low this week - that helps with fat loss!`);
                      }
                      
                      // Sleep win (1-5 scale: 1=terrible, 5=great)
                      const sleep = checkIn?.sleep_quality || 0;
                      if (sleep >= 4) {
                        wins.push(`✅ Sleep quality was ${sleep === 5 ? 'excellent' : 'good'} - recovery is on point!`);
                      }
                      
                      // Always show at least one win
                      if (wins.length === 0) {
                        wins.push(`✅ You submitted your check-in - that's commitment to your progress!`);
                      }
                      
                      return wins.map((win, idx) => (
                        <p key={idx} className="text-green-800 text-sm">
                          {win}
                        </p>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Focus Areas Section */}
        {(() => {
          const focusAreas = [];
          
          // Consistency issues (1-5 scale)
          const rawConsistency = checkIn?.consistency || 0;
          if (rawConsistency <= 2) {
            focusAreas.push({
              title: 'Consistency was lower than ideal this week',
              tip: rawConsistency === 1 
                ? 'Start small - focus on hitting just breakfast and lunch consistently. Small wins build momentum.'
                : 'Try meal prepping on Sunday. Even just 2-3 meals makes a huge difference.'
            });
          }
          
          // Weekend adherence issues
          const weekendAdh = checkIn?.weekend_adherence || 0;
          if (weekendAdh < 60) {
            focusAreas.push({
              title: `Weekend adherence was ${weekendAdh}%`,
              tip: "Plan one 'fun meal' on Saturday so you don't feel restricted. Structure breakfast and lunch to stay on track."
            });
          }
          
          // Hunger issues (1-5 scale: 1=starving, 5=satisfied)
          const hunger = checkIn?.hunger || 0;
          if (hunger <= 2) {
            focusAreas.push({
              title: 'Hunger levels were high this week',
              tip: 'Add more volume - vegetables, lean protein, and fiber help you stay full longer without extra calories.'
            });
          }
          
          // Energy issues (1-5 scale: 1=exhausted, 5=energized)
          const energy = checkIn?.energy || 0;
          if (energy <= 2) {
            focusAreas.push({
              title: 'Energy was low this week',
              tip: 'Make sure you\'re hitting your protein targets. Also check your sleep and hydration - aim for 2-3L water daily.'
            });
          }
          
          // Stress issues (1-5 scale: 1=very stressed, 5=very calm)
          const stress = checkIn?.stress_level || 0;
          if (stress <= 2) {
            focusAreas.push({
              title: 'Stress was high this week',
              tip: 'High stress can affect fat loss. Try 10-minute walks or deep breathing exercises before bed.'
            });
          }
          
          // Sleep issues (1-5 scale: 1=terrible, 5=great)
          const sleep = checkIn?.sleep_quality || 0;
          if (sleep <= 2) {
            focusAreas.push({
              title: 'Sleep quality was poor this week',
              tip: 'Aim for 7-8 hours. Try cutting screen time 1 hour before bed and keeping your room cool and dark.'
            });
          }
          
          // Only show if there are focus areas
          if (focusAreas.length === 0) {
            return null;
          }
          
          return (
            <section className="mb-8">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl">💡</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 text-lg mb-3">
                        Focus Areas This Week
                      </h3>
                      <div className="space-y-4">
                        {focusAreas.slice(0, 3).map((area, idx) => (
                          <div key={idx} className="bg-white/70 rounded-lg p-4 border border-blue-100">
                            <h4 className="font-medium text-blue-800 mb-2">
                              {area.title}
                            </h4>
                            <p className="text-blue-700 text-sm">
                              <span className="font-medium">Tip:</span> {area.tip}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          );
        })()}

        {/* Coach Review Status Section */}
        <section className="mb-8">
          <Card className={`${
            checkIn?.review_status === 'reviewed'
              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
              : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  checkIn?.review_status === 'reviewed'
                    ? 'bg-green-100'
                    : 'bg-purple-100'
                }`}>
                  <span className="text-2xl">{checkIn?.review_status === 'reviewed' ? '✅' : '📋'}</span>
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold text-lg mb-2 ${
                    checkIn?.review_status === 'reviewed'
                      ? 'text-green-900'
                      : 'text-purple-900'
                  }`}>
                    {checkIn?.review_status === 'reviewed' ? 'Coach Review Complete' : 'Coach Review Status'}
                  </h3>
                  
                  {checkIn?.review_status === 'reviewed' ? (
                    <>
                      <p className="text-green-800 text-sm leading-relaxed mb-2">
                        Coach reviewed your check-in on <strong>{new Date(checkIn.reviewed_at).toLocaleDateString()}</strong>
                      </p>
                      
                      {/* Show tier changes if any */}
                      {checkIn?.tier_changes && checkIn.tier_changes.length > 0 && (
                        <div className="bg-white/70 rounded-lg p-3 border border-green-100 mb-3">
                          <p className="text-sm font-medium text-green-900 mb-2">📊 Meal Plan Updated</p>
                          {checkIn.tier_changes.map((change, idx) => (
                            <p key={idx} className="text-xs text-green-800">
                              • {change.meal}: {change.oldTier} → {change.newTier} kcal
                            </p>
                          ))}
                        </div>
                      )}
                      
                      {/* Show coach feedback if any */}
                      {checkIn?.coach_feedback && (
                        <div className="bg-white/70 rounded-lg p-3 border border-green-100">
                          <p className="text-sm font-medium text-green-900 mb-1">💬 Coach Feedback:</p>
                          <p className="text-sm text-green-800 italic">"{checkIn.coach_feedback}"</p>
                        </div>
                      )}
                      
                      {!checkIn?.coach_feedback && (!checkIn?.tier_changes || checkIn.tier_changes.length === 0) && (
                        <p className="text-green-700 text-sm">
                          No adjustments needed this week - keep doing what you're doing!
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-purple-800 text-sm leading-relaxed">
                        Your check-in has been submitted! Coach {user?.coach_name || 'Knut'} reviews all check-ins on <strong>Fridays</strong> and will update your meal plan if needed.
                      </p>
                      <p className="text-purple-700 text-sm mt-2">
                        You'll be notified of any changes to your plan.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Cycle Note (if applicable) */}
        {showCycleNote && (
          <section className="mb-8">
            <Card className="bg-pink-50 border-pink-100">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">🌸</div>
                  <div>
                    <h4 className="font-medium text-pink-800 mb-1">
                      PMS Week
                    </h4>
                    <p className="text-sm text-pink-700">
                      Extra hunger and water weight fluctuations are totally normal this week. 
                      Be kind to yourself — your body is doing its thing.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Comfort Metrics Section */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Heart className="h-5 w-5 text-gray-400" />
            How You Felt
          </h3>
          
          <div className="grid grid-cols-3 gap-3">
            <ComfortTile type="hunger" score={hungerScore} />
            <ComfortTile type="energy" score={energyScore} />
            <ComfortTile type="stress" score={stressScore} />
          </div>
        </section>

        {/* Notes Section (if any) */}
        {checkIn?.notes && (
          <section className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Your Notes
            </h3>
            <Card>
              <CardContent className="p-4">
                <p className="text-gray-600 italic">&ldquo;{checkIn.notes}&rdquo;</p>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Footer CTA */}
        <div className="text-center py-6">
          <p className="text-gray-500 text-sm mb-4">
            Keep up the great work! See you next week. 💪
          </p>
          <Button 
            variant="outline"
            onClick={() => navigate('/app/meal-plan')}
          >
            Go to your meal plan
          </Button>
        </div>
      </div>
    </div>
  );
}
